/**
 * AI Colleague Chat Panel
 *
 * The main chat interface for conversing with AI colleagues.
 * Supports text messages with file attachments (images, PDFs).
 */
import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import { Typography, Input, Button, Space, Tag, Spin, Alert, Tooltip, Image } from 'antd';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  SendOutlined,
  ReloadOutlined,
  StopOutlined,
  ArrowLeftOutlined,
  ClearOutlined,
  PaperClipOutlined,
  CloseCircleFilled,
  FilePdfOutlined,
  SwapOutlined
} from '@ant-design/icons';
import type { Message, ColleagueId, AIColleagueContext, MessageAttachment, HandoffSuggestion } from '@/hooks/ai-colleagues';
import { SUPPORTED_ATTACHMENT_TYPES } from '@/hooks/ai-colleagues';
import { Link } from '@/lib/router';
import { COLLEAGUES } from './ColleagueSelector';
import styles from './ChatPanel.module.less';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

const UUID_PATTERN = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;
const HIDDEN_INTERNAL_ID_LABEL = '[internal ID hidden]';
const MARKDOWN_LINK_PATTERN = /\[([^\]]+)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/g;

const hideInternalIds = (value: string): string => value.replace(UUID_PATTERN, HIDDEN_INTERNAL_ID_LABEL);

const isSafeInternalRoute = (href: string): boolean => href.startsWith('/') && !href.startsWith('//');
const isSafeExternalLink = (href: string): boolean => /^(https?:|mailto:)/i.test(href);

const sanitizeMarkdownForDisplay = (value: string): string => {
  const preservedLinks: string[] = [];
  const withPlaceholders = value.replace(MARKDOWN_LINK_PATTERN, (match, label: string, href: string) => {
    if (!isSafeInternalRoute(href) && !isSafeExternalLink(href)) {
      return match;
    }

    const placeholder = `__DIGITAL_STAFF_LINK_${preservedLinks.length}__`;
    preservedLinks.push(`[${hideInternalIds(label)}](${href})`);
    return placeholder;
  });

  return hideInternalIds(withPlaceholders).replace(/__DIGITAL_STAFF_LINK_(\d+)__/g, (_match, index: string) => {
    return preservedLinks[Number(index)] ?? '';
  });
};

const getNodeText = (children: React.ReactNode): string => {
  if (typeof children === 'string' || typeof children === 'number') {
    return String(children);
  }

  if (Array.isArray(children)) {
    return children.map(getNodeText).join('');
  }

  return '';
};

const getInternalRouteActionTestId = (label: string): string => {
  const normalizedLabel = label.trim().toLowerCase();

  if (normalizedLabel === 'open record') {
    return 'digital-staff-open-record-action';
  }

  if (normalizedLabel === 'open queue') {
    return 'digital-staff-open-queue-action';
  }

  return 'digital-staff-internal-route-action';
};

const getAttachmentDisplayName = (name: string): string => {
  const safeName = hideInternalIds(name);
  if (safeName.includes(HIDDEN_INTERNAL_ID_LABEL)) {
    return safeName;
  }

  return safeName.length > 16 ? `${safeName.slice(0, 13)}...` : safeName;
};

type ChatPanelProps = {
  colleagueId: ColleagueId;
  messages: Message[];
  input: string;
  isLoading: boolean;
  error: Error | null;
  context?: AIColleagueContext;
  /** Pending attachments */
  attachments?: MessageAttachment[];
  onInputChange: (value: string) => void;
  onSend: () => void;
  onStop: () => void;
  onReload: () => void;
  onClear: () => void;
  onBack: () => void;
  /** Add files as attachments */
  onAddAttachments?: (files: File[]) => Promise<void>;
  /** Remove an attachment */
  onRemoveAttachment?: (id: string) => void;
  /** Switch to a suggested colleague and continue the original question */
  onSwitchColleague?: (suggestion: HandoffSuggestion) => void;
  quickActions?: Array<{ label: string; prompt: string }>;
  /** External ref for input auto-focus */
  inputRef?: React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
};

/**
 * Attachment thumbnail component
 */
const AttachmentThumbnail: React.FC<{ 
  attachment: MessageAttachment; 
  onRemove?: () => void;
  size?: 'small' | 'medium';
}> = ({ attachment, onRemove, size = 'medium' }) => {
  const isImage = attachment.mimeType.startsWith('image/');
  const isPdf = attachment.mimeType === 'application/pdf';
  const thumbnailSize = size === 'small' ? 48 : 64;

  return (
    <div className={`${styles.attachmentThumbnail} ${styles[size]}`}>
      {isImage && attachment.previewUrl ? (
        <Image
          src={attachment.previewUrl}
          alt={attachment.name}
          width={thumbnailSize}
          height={thumbnailSize}
          className={styles.attachmentImage}
          preview={{ mask: null }}
        />
      ) : isImage && attachment.data ? (
        <Image
          src={`data:${attachment.mimeType};base64,${attachment.data}`}
          alt={attachment.name}
          width={thumbnailSize}
          height={thumbnailSize}
          className={styles.attachmentImage}
          preview={{ mask: null }}
        />
      ) : isPdf ? (
        <div className={styles.pdfThumbnail}>
          <FilePdfOutlined className={styles.pdfIcon} />
          <span className={styles.fileName}>{getAttachmentDisplayName(attachment.name)}</span>
        </div>
      ) : (
        <div className={styles.fileThumbnail}>
          <PaperClipOutlined className={styles.fileIcon} />
          <span className={styles.fileName}>{getAttachmentDisplayName(attachment.name)}</span>
        </div>
      )}
      {onRemove && (
        <button
          aria-label={`Remove ${getAttachmentDisplayName(attachment.name)}`}
          className={styles.removeButton}
          data-testid="digital-staff-remove-attachment-button"
          onClick={onRemove}
          type="button"
        >
          <CloseCircleFilled />
        </button>
      )}
    </div>
  );
};

const SafeMarkdownMessage: React.FC<{ content: string }> = ({ content }) => (
  <Typography className={styles.markdownText}>
    <ReactMarkdown
      skipHtml
      remarkPlugins={[remarkGfm]}
      components={{
        a: ({ href, children }) => {
          const normalizedHref = href ?? '';
          const label = hideInternalIds(getNodeText(children));

          if (isSafeInternalRoute(normalizedHref)) {
            return (
              <Link
                to={normalizedHref}
                className={styles.internalRouteAction}
                aria-label={label}
                data-testid={getInternalRouteActionTestId(label)}
              >
                {children}
              </Link>
            );
          }

          if (!isSafeExternalLink(normalizedHref)) {
            return <Text>{children}</Text>;
          }

          return (
            <Typography.Link href={normalizedHref} target="_blank" rel="noopener noreferrer">
              {children}
            </Typography.Link>
          );
        },
        p: ({ children }) => (
          <Paragraph className={styles.markdownParagraph}>
            {children}
          </Paragraph>
        ),
        table: ({ children }) => (
          <div className={styles.markdownTableWrapper}>
            <table>{children}</table>
          </div>
        )
      }}
    >
      {sanitizeMarkdownForDisplay(content)}
    </ReactMarkdown>
  </Typography>
);

const HandoffAction: React.FC<{
  suggestion: HandoffSuggestion;
  onSwitchColleague?: (suggestion: HandoffSuggestion) => void;
}> = ({ suggestion, onSwitchColleague }) => {
  if (!onSwitchColleague) {
    return null;
  }

  return (
    <Space className={styles.handoffAction} size={8} wrap>
      <Text type="secondary" className={styles.handoffLabel}>
        Right specialist: {suggestion.targetName}
      </Text>
      <Button
        type="primary"
        size="small"
        icon={<SwapOutlined />}
        aria-label={`Ask ${suggestion.targetName} about ${suggestion.reason}`}
        data-testid={`digital-staff-handoff-${suggestion.targetColleagueId}-button`}
        onClick={() => onSwitchColleague(suggestion)}
      >
        Ask {suggestion.targetName}
      </Button>
    </Space>
  );
};

const MessageBubble: React.FC<{
  message: Message;
  colleagueIcon?: string;
  onSwitchColleague?: (suggestion: HandoffSuggestion) => void;
}> = ({
  message,
  colleagueIcon,
  onSwitchColleague
}) => {
  const isUser = message.role === 'user';

  return (
    <div className={`${styles.messageBubble} ${isUser ? styles.user : styles.assistant}`}>
      {!isUser && colleagueIcon && <div className={styles.avatar}>{colleagueIcon}</div>}
      <div className={styles.messageContent}>
        {/* Show attachments if present */}
        {message.attachments && message.attachments.length > 0 && (
          <div className={styles.messageAttachments}>
            {message.attachments.map((att) => (
              <AttachmentThumbnail key={att.id} attachment={att} size="small" />
            ))}
          </div>
        )}
        <div className={styles.messageText}>
          {message.content ? (
            isUser ? (
              <span className={styles.plainMessage}>{hideInternalIds(message.content)}</span>
            ) : (
              <SafeMarkdownMessage content={message.content} />
            )
          ) : (
            <span className={styles.typing}>
              <Spin size="small" /> Thinking...
            </span>
          )}
        </div>
        {message.toolCalls && message.toolCalls.length > 0 && (
          <div className={styles.toolCalls}>
            {message.toolCalls.map((tc) => (
              <Tag key={tc.id} color="blue" className={styles.toolTag}>
                🔧 {tc.name}
              </Tag>
            ))}
          </div>
        )}
        {!isUser && message.handoffSuggestion && (
          <HandoffAction suggestion={message.handoffSuggestion} onSwitchColleague={onSwitchColleague} />
        )}
      </div>
    </div>
  );
};

const ContextBanner: React.FC<{ context: AIColleagueContext }> = ({ context }) => {
  const contextIcons: Record<string, string> = {
    case: '📁',
    screening: '🔍',
    instruction: '💳',
    ticket: '🎫',
    user: '👤',
    solution: '🏛️',
    card: '💳',
    cardList: '💳',
    dashboard: '📊',
    feesPricing: '💰',
    digitalDeposit: '🔗',
    digitalWithdraw: '🔗',
    digitalDepositQueue: '🔗',
    digitalWithdrawQueue: '🔗'
  };

  const icon = contextIcons[context.type || ''] || '📋';

  const contextLabels: Record<string, string> = {
    cardList: 'Cards',
    digitalDeposit: 'Digital Deposit',
    digitalWithdraw: 'Digital Withdraw',
    feesPricing: 'Fees & Pricing',
    digitalDepositQueue: 'Pending Digital Deposits',
    digitalWithdrawQueue: 'Digital Withdrawals'
  };

  // Use tabTitle if available, otherwise fall back to type label
  const typeLabel = context.type
    ? contextLabels[context.type] || context.type.charAt(0).toUpperCase() + context.type.slice(1)
    : undefined;
  const displayTitle = context.tabTitle || typeLabel;

  if (!displayTitle) return null;

  return (
    <div className={styles.contextBanner}>
      <div className={styles.contextIcon}>{icon}</div>
      <div className={styles.contextInfo}>
        <Text className={styles.contextLabel}>Currently viewing</Text>
        <Text strong className={styles.contextTitle}>{displayTitle}</Text>
      </div>
    </div>
  );
};

export const ChatPanel: React.FC<ChatPanelProps> = ({
  colleagueId,
  messages,
  input,
  isLoading,
  error,
  context,
  attachments = [],
  onInputChange,
  onSend,
  onStop,
  onReload,
  onClear,
  onBack,
  onAddAttachments,
  onRemoveAttachment,
  onSwitchColleague,
  quickActions = [],
  inputRef: externalInputRef
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const internalInputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Use external ref if provided, otherwise use internal
  const inputRef = externalInputRef as React.RefObject<HTMLTextAreaElement> | undefined ?? internalInputRef;

  const colleague = useMemo(
    () => COLLEAGUES.find((c) => c.id === colleagueId) || COLLEAGUES.find((c) => c.id === 'riley'),
    [colleagueId]
  );

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isLoading && (input.trim() || attachments.length > 0)) {
        onSend();
      }
    }
  };

  const handleQuickAction = (prompt: string) => {
    onInputChange(prompt);
    setTimeout(() => onSend(), 0);
  };

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0 && onAddAttachments) {
      await onAddAttachments(files);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  }, [onAddAttachments]);

  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Check if we can send (either have text or attachments)
  const canSend = !isLoading && (input.trim() || attachments.length > 0);

  // Accepted file types for the input
  const acceptedTypes = SUPPORTED_ATTACHMENT_TYPES.join(',');

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <Button
          type="text"
          aria-label="Back to Digital Staff selection"
          data-testid="digital-staff-back-button"
          icon={<ArrowLeftOutlined />}
          onClick={onBack}
        />
        <div className={styles.headerInfo}>
          <span className={styles.colleagueIcon}>{colleague?.icon}</span>
          <div>
            <Text strong>{colleague?.name}</Text>
            <Text type="secondary" className={styles.colleagueRole}>
              {colleague?.role}
            </Text>
          </div>
        </div>
        <Space>
          <Tooltip title="Clear conversation">
            <Button
              type="text"
              aria-label="Clear Digital Staff conversation"
              data-testid="digital-staff-clear-button"
              icon={<ClearOutlined />}
              onClick={onClear}
              disabled={messages.length === 0}
            />
          </Tooltip>
        </Space>
      </div>

      {/* Context Banner */}
      {context?.type && <ContextBanner context={context} />}

      {/* Messages */}
      <div className={styles.messagesContainer}>
        {messages.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.welcomeIcon}>{colleague?.icon}</div>
            <Text strong className={styles.welcomeTitle}>Hey! I'm {colleague?.name}</Text>
            <Paragraph type="secondary" className={styles.welcomeDescription}>
              {colleague?.description}. How can I help you today?
            </Paragraph>

            {/* Quick Actions */}
            {quickActions.length > 0 && (
              <div className={styles.quickActions}>
                {quickActions.map((action, idx) => (
                  <Button
                    key={idx}
                    type="default"
                    size="small"
                    onClick={() => handleQuickAction(action.prompt)}
                  >
                    {action.label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                colleagueIcon={colleague?.icon}
                onSwitchColleague={onSwitchColleague}
              />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Error */}
      {error && (
        <Alert
          type="error"
          message={error.message}
          showIcon
          closable
          className={styles.errorAlert}
        />
      )}

      {/* Pending Attachments Preview */}
      {attachments.length > 0 && (
        <div className={styles.attachmentsPreview}>
          {attachments.map((att) => (
            <AttachmentThumbnail
              key={att.id}
              attachment={att}
              onRemove={onRemoveAttachment ? () => onRemoveAttachment(att.id) : undefined}
            />
          ))}
        </div>
      )}

      {/* Input */}
      <div className={styles.inputContainer}>
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedTypes}
          multiple
          onChange={handleFileSelect}
          aria-label="Upload Digital Staff attachment"
          className={styles.fileInput}
          data-testid="digital-staff-file-input"
        />
        
        {/* Attach button */}
        {onAddAttachments && (
          <Tooltip title="Attach image or PDF">
            <Button
              type="text"
              aria-label="Attach image or PDF"
              data-testid="digital-staff-attach-button"
              icon={<PaperClipOutlined />}
              onClick={handleAttachClick}
              disabled={isLoading}
              className={styles.attachButton}
            />
          </Tooltip>
        )}

        <TextArea
          ref={inputRef}
          value={input}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={`Ask ${colleague?.name} anything...`}
          aria-label="Digital Staff message"
          data-testid="digital-staff-chat-input"
          autoSize={{ minRows: 1, maxRows: 4 }}
          disabled={isLoading}
          className={styles.input}
        />
        <Space className={styles.inputActions}>
          {isLoading ? (
            <Button
              aria-label="Stop Digital Staff response"
              data-testid="digital-staff-stop-button"
              icon={<StopOutlined />}
              onClick={onStop}
              danger
            >
              Stop
            </Button>
          ) : (
            <>
              {messages.length > 0 && (
                <Tooltip title="Regenerate last response">
                  <Button
                    aria-label="Regenerate Digital Staff response"
                    data-testid="digital-staff-reload-button"
                    icon={<ReloadOutlined />}
                    onClick={onReload}
                    disabled={isLoading}
                  />
                </Tooltip>
              )}
              <Button
                type="primary"
                data-testid="digital-staff-send-button"
                icon={<SendOutlined />}
                onClick={onSend}
                disabled={!canSend}
              >
                Send
              </Button>
            </>
          )}
        </Space>
      </div>
    </div>
  );
};

export default ChatPanel;
